import { getToken } from "firebase/app-check";
import { appCheck, auth } from "./firebase";
import type { IntakeSource } from "../components/intakeDetails/types";
import type { AllowlistRole } from "./auth";
import { getBetaPasswordHeader } from "./betaGate";
import type { BetaRoute } from "./betaGate";
import type { McpKey, TemplateChild } from "./types";

const devFunctionsUrl = "http://127.0.0.1:5001/dit-oaip-poc-ai-projects/us-east1";
const baseUrl =
  import.meta.env.VITE_FUNCTIONS_BASE_URL || (import.meta.env.DEV ? devFunctionsUrl : "/api");

// AI-handler base. In dev these MUST flow through the Vite proxy at `/api/...`
// so they hit the AI sidecar on :5050 (firebase-tools#6628 — gunicorn workers
// crash on macOS the moment they construct a gRPC-backed Google client).
// In prod we use the same `/api` rewrite (Hosting handles it) or whatever
// VITE_FUNCTIONS_BASE_URL the deploy was configured with. `workflowsHandler`
// is registered in scripts/ai_sidecar.py, so all of its actions
// (get_workflow, create_workflow, advance_stage, ...) need to be routed
// through the sidecar — not just the AI-touching `generate_brief`.
const aiBaseUrl = import.meta.env.VITE_FUNCTIONS_BASE_URL || "/api";

/**
 * Defensive JSON parser. The Functions emulator returns an HTML/text page
 * (starts with `Function g…` or similar) when a gunicorn worker dies — the
 * default `response.json()` then throws `Unexpected token 'F'… is not valid
 * JSON`, which is useless to humans. Replace it with a clearer error that
 * surfaces the response status and the first slice of the body. Also throws
 * a friendly Error on any non-2xx response so callers can `try/catch` once
 * and surface the message via a toast.
 */
async function parseJsonSafe<T = any>(response: Response): Promise<T> {
  const text = await response.text();
  let parsed: any = undefined;
  let parseErr: Error | null = null;
  if (text) {
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      parseErr = e instanceof Error ? e : new Error(String(e));
    }
  }

  if (!response.ok) {
    const message =
      (parsed && typeof parsed === "object" && parsed.error) ||
      (parseErr ? `Service error (${response.status}): ${text.slice(0, 120)}` : null) ||
      `Service error (${response.status})`;
    throw new Error(String(message));
  }

  if (parseErr) {
    throw new Error(
      "Service returned a non-JSON response — likely an emulator/sidecar issue. Check the Functions logs.",
    );
  }
  return (parsed ?? {}) as T;
}

/** Like parseJsonSafe but returns null on 404 instead of throwing. */
async function parseJsonOrNull<T = any>(response: Response): Promise<T | null> {
  if (response.status === 404) return null;
  return parseJsonSafe<T>(response);
}

async function getAppCheckHeader(): Promise<Record<string, string>> {
  if (!appCheck) return {};
  try {
    const result = await getToken(appCheck, /* forceRefresh */ false);
    return { "X-Firebase-AppCheck": result.token };
  } catch {
    return {};
  }
}

/** Headers for authenticated calls: Bearer ID token + App Check token (if available). */
async function getAuthHeaders(): Promise<Record<string, string>> {
  const [appCheckHeader, token] = await Promise.all([
    getAppCheckHeader(),
    auth.currentUser?.getIdToken(),
  ]);
  return token ? { Authorization: `Bearer ${token}`, ...appCheckHeader } : appCheckHeader;
}

/**
 * Headers for the beta-gated public chatbot endpoints (/agency, /vendor).
 * Layers the per-route X-Apex-Beta-Password header (from sessionStorage,
 * stored after a successful gate unlock) on top of the App Check header.
 */
async function getBetaGatedHeaders(route: BetaRoute): Promise<Record<string, string>> {
  const appCheckHeader = await getAppCheckHeader();
  return { ...appCheckHeader, ...getBetaPasswordHeader(route) };
}

type StreamHandlers<TMeta = any> = {
  onDelta?: (chunk: string) => void;
  onThought?: (chunk: string) => void;
  onMeta?: (meta: TMeta) => void;
};

async function consumeSseStream<TMeta = any>(
  response: Response,
  handlers: StreamHandlers<TMeta> = {}
): Promise<{ text: string; meta?: TMeta }> {
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "AI stream failed");
  }

  if (!response.body) {
    throw new Error("Streaming not supported by the response");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let text = "";
  let meta: TMeta | undefined;

  const processBuffer = (dataBuffer: string) => {
    const normalized = dataBuffer.replace(/\r\n/g, "\n");
    const parts = normalized.split("\n\n");
    return { parts: parts.slice(0, -1), remaining: parts[parts.length - 1] ?? "" };
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const { parts, remaining } = processBuffer(buffer);
    buffer = remaining;

    for (const part of parts) {
      const lines = part.split("\n");
      for (const line of lines) {
        if (!line.startsWith("data:")) continue;
        const data = line.replace(/^data:\s?/, "").trim();
        if (!data) continue;
        if (data === "[DONE]") {
          return { text, meta };
        }
        const parsed = JSON.parse(data) as {
          delta?: string;
          thought?: string;
          error?: string;
          meta?: TMeta;
        };
        if (parsed.error) {
          throw new Error(parsed.error);
        }
        if (parsed.delta) {
          text += parsed.delta;
          handlers.onDelta?.(parsed.delta);
        }
        if (parsed.thought) {
          handlers.onThought?.(parsed.thought);
        }
        if (parsed.meta) {
          meta = parsed.meta;
          handlers.onMeta?.(parsed.meta);
        }
      }
    }
  }

  return { text, meta };
}

/**
 * Public endpoint — gates the Firebase password-reset email on the
 * server-side allowlist. Always resolves on a 2xx (no oracle leak); the
 * caller can show "check your email" regardless of whether anything was sent.
 * Throws only on rate-limit (429) or service misconfig (503).
 */
export async function sendPasswordReset(email: string): Promise<void> {
  const appCheckHeader = await getAppCheckHeader();
  const response = await fetch(`${baseUrl}/apexAuthHandler`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...appCheckHeader },
    body: JSON.stringify({ action: "sendPasswordReset", email }),
  });
  if (!response.ok) {
    await parseJsonSafe(response);
  }
}

export async function postAgencyIntake(payload: Record<string, unknown>) {
  const publicHeaders = await getBetaGatedHeaders("agency");
  const response = await fetch(`${baseUrl}/apexAgenciesHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...publicHeaders,
    },
    body: JSON.stringify(payload),
  });
  return response.json();
}

export async function postVendorIntake(payload: Record<string, unknown>) {
  const publicHeaders = await getBetaGatedHeaders("vendor");
  const response = await fetch(`${baseUrl}/apexVendorsHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...publicHeaders,
    },
    body: JSON.stringify(payload),
  });
  return response.json();
}

export interface AgencyIntakeFinalizeResult {
  intakeId: string;
  status: string;
  intakeName: string;
  intakeDescription: string;
}

export class IntakeNameGenerationError extends Error {
  constructor() {
    super("name_generation_failed");
    this.name = "IntakeNameGenerationError";
  }
}

/**
 * Submit Intake — explicit user action that flips the intake to
 * `status="Submitted"`, generates the intake name/description, and
 * creates the workflow (OAIP-68).
 *
 * Throws ``IntakeNameGenerationError`` on a 502 from the server so the
 * UI can show a retry CTA without conflating it with other errors.
 */
export async function submitAgencyIntake(args: {
  intakeId: string;
  draftAnswers: Array<{ questionId: string; answer: string }>;
}): Promise<AgencyIntakeFinalizeResult> {
  const publicHeaders = await getBetaGatedHeaders("agency");
  const response = await fetch(`${baseUrl}/apexAgencyIntakeFinalizeHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...publicHeaders,
    },
    body: JSON.stringify(args),
  });
  if (response.status === 502) {
    throw new IntakeNameGenerationError();
  }
  return parseJsonSafe<AgencyIntakeFinalizeResult>(response);
}

export interface VendorIntakeFinalizeResult {
  intakeId: string;
  status: string;
  vendorProfile: string;
  capabilityTags: string[];
}

/**
 * Confirm Profile — user-confirmed save of the AI-drafted vendor
 * profile (OAIP-68). Flips ``status`` to ``profile-complete``; the
 * "Review & Sign Agreement" step still runs separately.
 */
export async function submitVendorIntake(args: {
  intakeId: string;
  vendorProfile: string;
  capabilityTags: string[];
}): Promise<VendorIntakeFinalizeResult> {
  const publicHeaders = await getBetaGatedHeaders("vendor");
  const response = await fetch(`${baseUrl}/apexVendorIntakeFinalizeHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...publicHeaders,
    },
    body: JSON.stringify(args),
  });
  return parseJsonSafe<VendorIntakeFinalizeResult>(response);
}

export async function askAi(
  prompt: string,
  context?: string,
  options?: {
    sessionId?: string;
    history?: Array<{ role: "assistant" | "user"; text: string }>;
    toolsEnabled?: boolean;
    attachments?: Array<{ mimeType: string; data: string }>;
  }
) {
  const { text, meta } = await streamAi(prompt, context, undefined, options);
  return { message: text, ...meta };
}

export async function streamAi(
  prompt: string,
  context: string | undefined,
  onChunk?: (chunk: string) => void,
  options?: {
    sessionId?: string;
    history?: Array<{ role: "assistant" | "user"; text: string }>;
    toolsEnabled?: boolean;
    attachments?: Array<{ mimeType: string; data: string }>;
    onThought?: (chunk: string) => void;
    onMeta?: (meta: any) => void;
    onConfirmationRequired?: (confirmation: any) => void;
    model?: string;
    thinkingLevel?: "LOW" | "MEDIUM" | "HIGH";
    requireConfirmation?: boolean;
    confirmedTool?: { name: string; args: any };
    /** Optional page context (slice 8). Backend interpolates into the system prompt. */
    pageContext?: Record<string, unknown>;
    /** When true, no streaming; the call posts a cancel signal for the in-flight session (slice 9). */
    cancelled?: boolean;
  }
) {
  const authHeaders = await getAuthHeaders();
  // OAIP-154: the Apex chat must NOT go through Firebase Hosting in prod.
  // Hosting hard-caps any rewrite response at 60s (returns 504), and a heavy
  // multi-tool turn (thinking model + several tool rounds + a large answer)
  // routinely runs 60-90s — the function itself is allowed 540s. So in prod we
  // hit the function's direct cloudfunctions.net URL (already in the CSP
  // connect-src; CORS allows the apex.oaip.nc.gov origin), which honors the
  // full 540s timeout. In dev VITE_AI_STREAM_BASE_URL is unset, so this falls
  // back to `baseUrl` and SSE keeps flowing through the Vite proxy → sidecar.
  const streamBaseUrl = import.meta.env.VITE_AI_STREAM_BASE_URL || baseUrl;
  const response = await fetch(`${streamBaseUrl}/apexAiHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...authHeaders,
    },
    body: JSON.stringify({
      prompt,
      context,
      stream: true,
      sessionId: options?.sessionId,
      history: options?.history,
      toolsEnabled: options?.toolsEnabled,
      inlineData: options?.attachments,
      model: options?.model,
      thinkingLevel: options?.thinkingLevel,
      requireConfirmation: options?.requireConfirmation,
      confirmedTool: options?.confirmedTool,
      pageContext: options?.pageContext ?? {},
      cancelled: options?.cancelled,
    }),
  });
  return consumeSseStream(response, {
    onDelta: onChunk,
    onThought: options?.onThought,
    onMeta: (meta) => {
      if (meta?.confirmation_required && options?.onConfirmationRequired) {
        options.onConfirmationRequired(meta.confirmation_required);
      } else if (options?.onMeta) {
        options.onMeta(meta);
      }
    },
  });
}

export async function streamAgencyIntake(
  payload: Record<string, unknown>,
  handlers: StreamHandlers = {}
) {
  const publicHeaders = await getBetaGatedHeaders("agency");
  const response = await fetch(`${baseUrl}/apexAgenciesHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...publicHeaders,
    },
    body: JSON.stringify({ ...payload, stream: true }),
  });

  return consumeSseStream(response, handlers);
}

export async function streamVendorIntake(
  payload: Record<string, unknown>,
  handlers: StreamHandlers = {}
) {
  const publicHeaders = await getBetaGatedHeaders("vendor");
  const response = await fetch(`${baseUrl}/apexVendorsHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...publicHeaders,
    },
    body: JSON.stringify({ ...payload, stream: true }),
  });

  return consumeSseStream(response, handlers);
}

export interface IntakeSchemaItem {
  id: string;
  stepTitle?: string;
  question?: string;
  description?: string;
  internal?: boolean;
  placeholder?: string;
  guidance?: string[];
  guidanceTitle?: string;
}

export async function getIntakeSchema(): Promise<IntakeSchemaItem[]> {
  const response = await fetch(`${baseUrl}/apexIntakeSchemaHandler`, { method: "GET" });
  if (!response.ok) {
    throw new Error(`Failed to fetch intake schema (${response.status})`);
  }
  return response.json();
}

export interface ManualIntakePayload {
  submitterName: string;
  submitterEmail: string;
  submitterAgency: string;
  source: IntakeSource;
  intakeName?: string;
  intakeDescription?: string;
  answers: Array<{
    questionId: string;
    stepTitle?: string;
    question?: string;
    answer: string;
  }>;
}

export async function createManualIntake(
  payload: ManualIntakePayload
): Promise<{ intakeId: string; intakeName: string }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${baseUrl}/apexManualIntakeCreateHandler`, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Failed to create intake (${response.status})`);
  }
  return response.json();
}

/**
 * OAIP-76: edit a `publicIntakes` doc through the audited backend handler.
 * Field allowlist is enforced server-side (`INTAKE_EDITABLE_FIELDS` in
 * `domains/intake_admin.py`) — extra fields are silently dropped.
 *
 * Use this instead of client-side `updateDoc(doc(db, "publicIntakes", id))`
 * so every staff edit lands in `auditLog` automatically.
 */
export async function updateIntake(
  intakeId: string,
  updates: Record<string, unknown>,
): Promise<{ ok: true; intakeId: string; fieldsUpdated: string[] }> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${baseUrl}/apexIntakeUpdateHandler`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    body: JSON.stringify({ intakeId, updates }),
  });
  return parseJsonSafe(response);
}

/**
 * OAIP-76: edit a `workflowBriefs.markdown` field through the audited
 * backend handler. Provenance fields (briefType, model, generatedAt, ...)
 * stay immutable; the handler stamps `editedAt` / `editedByEmail` for the
 * "last edited" hint surfaced in the brief viewer.
 */
export async function updateBrief(
  briefId: string,
  markdown: string,
): Promise<{ ok: true; briefId: string }> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${baseUrl}/apexBriefUpdateHandler`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    body: JSON.stringify({ briefId, markdown }),
  });
  return parseJsonSafe(response);
}

export async function listProjects() {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${baseUrl}/apexProjectsHandler`, {
    headers: {
      ...authHeaders,
    },
  });
  return response.json();
}

export async function createProject(payload: Record<string, unknown>) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${baseUrl}/apexProjectsHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
    },
    body: JSON.stringify(payload),
  });
  return response.json();
}

export async function deleteProject(id: string) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${baseUrl}/apexProjectsHandler`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
    },
    body: JSON.stringify({ id }),
  });
  return response.json();
}

/**
 * OAIP-114: edit a `projects` doc through the audited backend handler.
 * Field allowlist is enforced server-side (`PROJECT_EDITABLE_*_FIELDS` in
 * `domains/projects.py`) — extra fields are silently dropped. When `status`
 * changes the backend also writes the auto-comment + structured audit entry
 * for the transition.
 *
 * Use this instead of client-side `updateDoc(doc(db, "projects", id))` so
 * every edit lands in `auditLog` with actor + diff.
 */
export async function updateProject(
  projectId: string,
  updates: Record<string, unknown>,
): Promise<{ id: string; fieldsUpdated: string[] }> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${baseUrl}/apexProjectsHandler`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json", ...authHeaders },
    body: JSON.stringify({ id: projectId, ...updates }),
  });
  return parseJsonSafe(response);
}
export async function listUsers() {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${baseUrl}/apexListUsersHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
    },
    body: JSON.stringify({}),
  });
  const data = await response.json();
  return data.users || [];
}

export interface AllowlistUser {
  email: string;
  role: AllowlistRole;
  addedBy?: string;
  addedAt?: string;
  lastModifiedBy?: string;
  lastModifiedAt?: string;
}

async function postAdminUsers(action: string, body: Record<string, unknown> = {}) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${baseUrl}/apexAdminUsersHandler`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    body: JSON.stringify({ action, ...body }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `adminUsers ${action} failed (${response.status})`);
  }
  return data;
}

export async function listAllowlistUsers(): Promise<AllowlistUser[]> {
  const data = await postAdminUsers("list");
  return (data.users || []) as AllowlistUser[];
}

export async function setUserRole(email: string, role: AllowlistRole): Promise<void> {
  await postAdminUsers("set_role", { email, role });
}

export async function removeUser(email: string): Promise<void> {
  await postAdminUsers("remove", { email });
}

export interface InviteUserResult {
  email: string;
  role: AllowlistRole;
  uid: string;
  actionLink: string;
}

export async function inviteUser(email: string, role: AllowlistRole): Promise<InviteUserResult> {
  const data = await postAdminUsers("invite", { email, role });
  return data as InviteUserResult;
}

export interface AuditLogEntry {
  id: string;
  ts: string;
  actorEmail: string;
  actorChannel: "web" | "apex" | "system";
  action: "create" | "update" | "delete";
  collection: string;
  docId: string;
  changedFields: string[];
  diff: Record<string, { before: unknown; after: unknown }>;
  beforeSnapshot?: Record<string, unknown>;
  afterSnapshot?: Record<string, unknown>;
  apexPrompt?: string;
  metadata?: Record<string, unknown>;
}

export interface AuditLogQueryParams {
  actors?: string[];
  channels?: ("web" | "apex" | "system")[];
  actions?: ("create" | "update" | "delete")[];
  collections?: string[];
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  limit?: number;
  cursor?: string | null;
}

export async function queryAuditLog(
  params: AuditLogQueryParams
): Promise<{ entries: AuditLogEntry[]; nextCursor: string | null }> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${baseUrl}/apexAuditLogQueryHandler`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    body: JSON.stringify(params),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `auditLogQuery failed (${response.status})`);
  }
  return {
    entries: (data.entries || []) as AuditLogEntry[],
    nextCursor: (data.nextCursor ?? null) as string | null,
  };
}
// All workflowsHandler calls flow through `aiBaseUrl` so the Vite dev proxy
// steers them at the sidecar (:5050) instead of the gunicorn-fork-crashing
// Functions emulator (:5001). See firebase-tools#6628.

/**
 * Fetch the workflow bundle for an intake. A 404 means "no workflow exists
 * yet" — that's the expected empty state, not an error, so callers shouldn't
 * see a toast for it. Returns null in that case; everything else either
 * succeeds with the parsed payload or throws a friendly error.
 */
export async function getWorkflow(intakeId: string) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(
    `${aiBaseUrl}/apexWorkflowsHandler?intakeId=${intakeId}`,
    { headers: { ...authHeaders } },
  );
  return parseJsonOrNull(response);
}

export async function createWorkflow(intakeId: string) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${aiBaseUrl}/apexWorkflowsHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
    },
    body: JSON.stringify({ action: "create_workflow", intakeId }),
  });
  return parseJsonSafe(response);
}

export async function advanceWorkflowStage(workflowId: string) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${aiBaseUrl}/apexWorkflowsHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
    },
    body: JSON.stringify({ action: "advance_stage", workflowId }),
  });
  return parseJsonSafe(response);
}

export async function addMeeting(payload: Record<string, unknown>) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${aiBaseUrl}/apexWorkflowsHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
    },
    body: JSON.stringify({ action: "add_meeting", ...payload }),
  });
  return parseJsonSafe(response);
}

export async function updateMeeting(meetingId: string, payload: Record<string, unknown>) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${aiBaseUrl}/apexWorkflowsHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
    },
    body: JSON.stringify({ action: "update_meeting", meetingId, ...payload }),
  });
  return parseJsonSafe(response);
}

export async function addScoring(payload: Record<string, unknown>) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${aiBaseUrl}/apexWorkflowsHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
    },
    body: JSON.stringify({ action: "add_scoring", ...payload }),
  });
  return parseJsonSafe(response);
}

export async function submitEvaluation(payload: {
  intakeId: string;
  scores: Record<string, { score: 1 | 2 | 3 | 4 | 5; notes?: string }>;
  overallNotes?: string;
}) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${aiBaseUrl}/apexWorkflowsHandler`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    body: JSON.stringify({ action: "submit_evaluation", ...payload }),
  });
  return parseJsonSafe(response);
}

export type VendorSelectionPayload =
  | { type: "vendor"; vendorId: string; vendorName: string }
  | { type: "build_internal"; reason?: string };

export async function selectVendor(payload: {
  intakeId: string;
  decision: VendorSelectionPayload;
}) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${aiBaseUrl}/apexWorkflowsHandler`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    body: JSON.stringify({ action: "select_vendor", ...payload }),
  });
  return parseJsonSafe(response);
}

/**
 * Update per-stage user-editable fields (Advisory Board notes, Leadership
 * "brief sent" checkbox, scoring-stage Risk Assessment). Whitelisted to
 * `notes`, `briefSentToLeadership`, and `riskAssessment` by the backend.
 *
 * For `riskAssessment` the server is the source of truth: it ignores any
 * client-supplied `riskLevel` and recomputes it from the 3 inputs using the
 * matrix in `2 - AI Use-Case Risk Definition.docx`.
 */
export async function updateStageData(
  workflowId: string,
  stageId: string,
  data: Partial<{
    notes: string;
    briefSentToLeadership: boolean;
    riskAssessment: {
      harmToAgency: "Minor" | "Moderate" | "Major" | "Severe";
      harmToPublic: "Minor" | "Moderate" | "Major" | "Severe";
      dataClassification: "Public" | "Internal" | "Confidential" | "Restricted";
    };
  }>,
) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${aiBaseUrl}/apexWorkflowsHandler`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    body: JSON.stringify({
      action: "update_stage_data",
      workflowId,
      stageId,
      data,
    }),
  });
  return parseJsonSafe(response);
}

/**
 * Download the leadership brief as a real Office file (Word or PowerPoint).
 * Server returns the binary; we trigger a browser download via blob URL.
 * (OAIP-91 — replaces the rasterized PDF that Leadership previously had.)
 */
async function downloadLeadershipBriefFile(
  intakeId: string,
  intakeName: string,
  variant: "docx" | "pptx",
): Promise<void> {
  const handler =
    variant === "docx"
      ? "apexLeadershipBriefDocxHandler"
      : "apexLeadershipBriefPptxHandler";
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${aiBaseUrl}/${handler}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    body: JSON.stringify({ intakeId }),
  });
  if (!response.ok) {
    let message = `Failed to download ${variant.toUpperCase()} (${response.status})`;
    try {
      const body = await response.json();
      if (body?.error) message = body.error;
    } catch {
      // Body wasn't JSON — keep the generic status-code message.
    }
    throw new Error(message);
  }
  const blob = await response.blob();
  // Guard against a silently-wrong body. If the Hosting rewrite for this
  // handler is missing, the same-origin `/api/...` request falls through to
  // the SPA catch-all (`"**" → /index.html`) and returns index.html with a
  // 200, which we'd otherwise save as a broken `.pptx`/`.docx` that Office
  // refuses to open (OAIP-121). Every OOXML file is a ZIP container that
  // begins with the "PK" local-file-header magic — verify it before handing
  // the user a download, so a routing/config gap surfaces as a clear error.
  const magic = new Uint8Array(await blob.slice(0, 2).arrayBuffer());
  const isOoxmlZip = magic[0] === 0x50 && magic[1] === 0x4b; // "PK"
  if (!isOoxmlZip) {
    throw new Error(
      `The ${variant.toUpperCase()} download came back in an unexpected format ` +
        `(${blob.type || "unknown content type"}). The server may be misconfigured — ` +
        `please report this to the OAIP team.`,
    );
  }
  const slug =
    (intakeName || intakeId)
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "leadership-brief";
  const filename = `${slug}-leadership-brief.${variant}`;
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function downloadLeadershipBriefDocx(
  intakeId: string,
  intakeName: string,
): Promise<void> {
  return downloadLeadershipBriefFile(intakeId, intakeName, "docx");
}

export function downloadLeadershipBriefPptx(
  intakeId: string,
  intakeName: string,
): Promise<void> {
  return downloadLeadershipBriefFile(intakeId, intakeName, "pptx");
}

export async function aggregateScores(intakeId: string) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${aiBaseUrl}/apexWorkflowsHandler`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    body: JSON.stringify({ action: "aggregate_scores", intakeId }),
  });
  return parseJsonSafe(response);
}

export async function recordDecision(payload: {
  workflowId: string;
  decision: "approved" | "declined" | "deferred";
  rationale: string;
}) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${aiBaseUrl}/apexWorkflowsHandler`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    body: JSON.stringify({ action: "advance_stage", ...payload }),
  });
  return parseJsonSafe(response);
}

export async function generateBrief(
  payload: { intakeId: string; briefType: "ai_advisory_board" | "leadership" },
  handlers: StreamHandlers<{ briefId: string; model: string; briefType: string }> = {}
) {
  const authHeaders = await getAuthHeaders();
  // SSE flows through the Vite proxy too — the proxy preserves chunked transfer.
  const response = await fetch(`${aiBaseUrl}/apexWorkflowsHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...authHeaders,
    },
    body: JSON.stringify({ action: "generate_brief", ...payload }),
  });
  return consumeSseStream(response, handlers);
}

export type SuggestedVendor = {
  vendorId: string;
  companyName: string;
  score: number;
  explanation: string;
  capabilityTags?: string[];
};

export async function matchVendors(
  intakeId: string
): Promise<{ count: number; suggestedVendors: SuggestedVendor[] }> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${baseUrl}/apexMatchVendorsHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
    },
    body: JSON.stringify({ intakeId }),
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Vendor matching failed");
  }
  return response.json();
}

export async function generateDailySummary(handlers: StreamHandlers = {}, context?: Record<string, unknown>) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${baseUrl}/apexGenerateDailySummaryHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...authHeaders,
    },
    body: JSON.stringify({ stream: true, context }),
  });
  return consumeSseStream(response, handlers);
}

export type ExecStatsResponse = {
  period: {
    key: string;
    label: string;
    start: string | null;
    end: string | null;
  };
  totals: {
    activeProjects: number;
    submittedIntakes: number;
    activeVendors: number;
    shipped: number;
  };
  funnel: { status: string; count: number }[];
  bands: { band: "active" | "onHold" | "rejected"; count: number }[];
  riskBands: { band: "low" | "medium" | "high" | "unscored"; count: number }[];
};

export async function getExecStats(period?: string): Promise<ExecStatsResponse> {
  const authHeaders = await getAuthHeaders();
  // baseUrl is "/api" in prod (relative — URL ctor rejects it without a base),
  // and an absolute http://… in dev. Passing window.location.origin as the
  // base is a no-op when the first arg is absolute, so this works for both.
  const url = new URL(`${baseUrl}/apexExecStatsHandler`, window.location.origin);
  if (period) url.searchParams.set("period", period);
  // cache:'no-store' so Chrome never reuses a stale entry. We got bit when an
  // earlier deploy was missing the /api rewrite and Hosting served index.html
  // with max-age=3600; the browser then served stale HTML for an hour even
  // after the rewrite shipped.
  const response = await fetch(url.toString(), {
    method: "GET",
    headers: authHeaders,
    cache: "no-store",
  });
  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || "Failed to load executive stats");
  }
  return response.json();
}

export async function streamBacklogThemes(
  handlers: StreamHandlers = {},
  opts: { force?: boolean } = {}
) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${baseUrl}/apexBacklogThemesHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...authHeaders,
    },
    body: JSON.stringify({ stream: true, force: !!opts.force }),
  });
  return consumeSseStream(response, handlers);
}

export async function generateExecBriefing(
  handlers: StreamHandlers = {},
  context?: Record<string, unknown>
) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${baseUrl}/apexExecBriefingHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...authHeaders,
    },
    body: JSON.stringify({ stream: true, context }),
  });
  return consumeSseStream(response, handlers);
}

export function todayKey(date: Date = new Date()): string {
  const utc = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  return utc.toISOString().slice(0, 10);
}
export async function checkRenewals() {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${baseUrl}/apexCheckRenewalsHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
    },
    body: JSON.stringify({}),
  });
  return response.json();
}

export async function ragSearch(query: string, collections?: string[], limit?: number) {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${baseUrl}/apexRagSearchHandler`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders,
    },
    body: JSON.stringify({ query, collections, limit }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "Search failed");
  }

  return response.json();
}

// ---------------------------------------------------------------------------
// Slice 3 gap-fill HTTP wrappers. Each mirrors the new backend handler in
// functions/domains/{vendor_intake,project_children,comments,notifications}.py
// so existing pages can call them instead of writing directly to Firestore —
// the backend wrappers stamp audit log entries and enforce role checks.
// ---------------------------------------------------------------------------

async function _send(
  url: string,
  method: "POST" | "PATCH" | "DELETE" | "GET",
  body?: Record<string, unknown>,
): Promise<unknown> {
  const authHeaders = await getAuthHeaders();
  const init: RequestInit = {
    method,
    headers: { "Content-Type": "application/json", ...authHeaders },
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  const response = await fetch(url, init);
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(
      (data as { error?: string }).error || `${method} ${url} failed (${response.status})`,
    );
  }
  return data;
}

export type ProjectChild = "stages" | "tasks" | "subtasks" | "metrics";

export const projectChildren = {
  add: (projectId: string, child: ProjectChild, data: Record<string, unknown>) =>
    _send(`${baseUrl}/apexProjectChildrenHandler`, "POST", { projectId, child, data }),
  update: (
    projectId: string,
    child: ProjectChild,
    id: string,
    updates: Record<string, unknown>,
  ) =>
    _send(`${baseUrl}/apexProjectChildrenHandler`, "PATCH", {
      projectId,
      child,
      id,
      updates,
    }),
  delete: (projectId: string, child: ProjectChild, id: string) =>
    _send(`${baseUrl}/apexProjectChildrenHandler`, "DELETE", { projectId, child, id }),
};

export const vendorsCrud = {
  update: (vendorId: string, updates: Record<string, unknown>) =>
    _send(`${baseUrl}/apexVendorsCrudHandler`, "PATCH", { vendorId, updates }),
  delete: (vendorId: string) =>
    _send(`${baseUrl}/apexVendorsCrudHandler`, "DELETE", { vendorId }),
};

// --- OAIP-44 project plan templates ----------------------------------------
// Admin CRUD on the template library + its children, routed through the audited
// Admin-SDK handlers (so every edit lands in `auditLog`). Reads of templates
// for the convert picker happen via Firestore `onSnapshot` directly.
export const projectTemplates = {
  create: (data: { name: string; description?: string; isDefault?: boolean }) =>
    _send(`${baseUrl}/apexProjectTemplateHandler`, "POST", data) as Promise<{
      ok: true;
      id: string;
    }>,
  update: (templateId: string, updates: Record<string, unknown>) =>
    _send(`${baseUrl}/apexProjectTemplateHandler`, "PATCH", { templateId, updates }),
  delete: (templateId: string) =>
    _send(`${baseUrl}/apexProjectTemplateHandler`, "DELETE", { templateId }),
};

export const projectTemplateChildren = {
  add: (templateId: string, child: TemplateChild, data: Record<string, unknown>) =>
    _send(`${baseUrl}/apexProjectTemplateChildrenHandler`, "POST", { templateId, child, data }),
  update: (
    templateId: string,
    child: TemplateChild,
    id: string,
    updates: Record<string, unknown>,
  ) =>
    _send(`${baseUrl}/apexProjectTemplateChildrenHandler`, "PATCH", {
      templateId,
      child,
      id,
      updates,
    }),
  delete: (templateId: string, child: TemplateChild, id: string) =>
    _send(`${baseUrl}/apexProjectTemplateChildrenHandler`, "DELETE", { templateId, child, id }),
};

export interface ConvertIntakeResult {
  ok: true;
  projectId: string;
  counts: { stages: number; tasks: number; subtasks: number; metrics: number };
}

/**
 * OAIP-44: convert an intake to a project through the audited backend handler,
 * optionally applying a plan template in the same call. `projectFields` is the
 * synthesised payload from the convert modal (validated server-side against the
 * project field allowlist). Replaces the legacy direct `addDoc(projects)` write.
 */
export async function convertIntakeToProject(payload: {
  intakeId: string;
  projectFields: Record<string, unknown>;
  templateId?: string | null;
  roleMap?: Record<string, string>;
}): Promise<ConvertIntakeResult> {
  return _send(
    `${baseUrl}/apexConvertIntakeToProjectHandler`,
    "POST",
    payload,
  ) as Promise<ConvertIntakeResult>;
}

export type CommentTarget = "intake" | "project";

export interface CommentRecord {
  id: string;
  message: string;
  author?: string;
  createdAt?: string;
  updatedAt?: string;
  intakeId?: string;
  projectId?: string;
}

export const commentsApi = {
  add: (targetType: CommentTarget, targetId: string, message: string, author?: string) =>
    _send(`${baseUrl}/apexCommentsHandler`, "POST", {
      targetType,
      targetId,
      message,
      ...(author ? { author } : {}),
    }),
  update: (commentId: string, message: string, targetType: CommentTarget = "intake") =>
    _send(`${baseUrl}/apexCommentsHandler`, "PATCH", { commentId, message, targetType }),
  delete: (commentId: string, targetType: CommentTarget = "intake") =>
    _send(`${baseUrl}/apexCommentsHandler`, "DELETE", { commentId, targetType }),
  // Convenience wrapper around list_comments tool — exposed via a dedicated
  // GET helper would require a new handler; the apex tool path also works for
  // the UI when threaded through aiHandler. Until then, callers continue to
  // read directly via Firestore listeners (intakeComments / projectComments).
};

export interface NotificationRecord {
  id: string;
  userId?: string;
  recipientEmail?: string;
  type?: string;
  title?: string;
  body?: string;
  link?: string;
  read?: boolean;
  dismissed?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export const notificationsApi = {
  list: (limit?: number, includeDismissed?: boolean) =>
    _send(`${baseUrl}/apexNotificationsHandler`, "POST", {
      action: "list",
      ...(limit ? { limit } : {}),
      ...(includeDismissed ? { includeDismissed } : {}),
    }) as Promise<{ notifications: NotificationRecord[]; count: number }>,
  markRead: (ids: string[]) =>
    _send(`${baseUrl}/apexNotificationsHandler`, "POST", { action: "mark_read", ids }),
  dismiss: (ids: string[]) =>
    _send(`${baseUrl}/apexNotificationsHandler`, "POST", { action: "dismiss", ids }),
  markAllRead: () =>
    _send(`${baseUrl}/apexNotificationsHandler`, "POST", { action: "mark_all_read" }),
};

// ---------------------------------------------------------------------------
// Slice 7: Chat history (Apex). The backend `chatsHandler` exposes a single
// POST endpoint with an `action` discriminator. The frontend never reads
// Firestore directly for chats — the handler enforces ownership + auth.
// ---------------------------------------------------------------------------

export interface ChatSummary {
  chatId: string;
  chatTitle: string;
  lastMessageAt?: string;
  messageCount?: number;
  updatedAt?: string;
  deleted?: boolean;
}

export interface PersistedChatMessage {
  role: "assistant" | "user";
  text: string;
  thoughts?: string;
  ts?: string;
}

export interface ChatDetail {
  chatId: string;
  chatTitle: string;
  messages: PersistedChatMessage[];
  lastMessageAt?: string;
  updatedAt?: string;
}

// ---------------------------------------------------------------------------
// OAIP-96: MCP API key management. Self-service for every signed-in
// allowlisted user (Settings → My API Keys). Backed by apexMcpKeysHandler.
// ---------------------------------------------------------------------------

export type { McpKey };

async function _postMcpKeys<T>(action: string, body: Record<string, unknown> = {}): Promise<T> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${baseUrl}/apexMcpKeysHandler`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    body: JSON.stringify({ action, ...body }),
  });
  return parseJsonSafe<T>(response);
}

export async function listMcpKeys(): Promise<McpKey[]> {
  const data = await _postMcpKeys<{ keys: McpKey[] }>("list");
  return data.keys || [];
}

export interface CreatedMcpKey {
  id: string;
  key: string;
  prefix: string;
}

export async function createMcpKey(label: string): Promise<CreatedMcpKey> {
  return _postMcpKeys<CreatedMcpKey>("create", { label });
}

export async function revokeMcpKey(id: string): Promise<void> {
  await _postMcpKeys("revoke", { id });
}

// ---------------------------------------------------------------------------
// OAIP-153 (INTERIM — replaced by Entra ID SSO): one-click OAuth consent.
// The /oauth/consent page POSTs the authorize params + the user's decision to
// apexOauthHandler; the backend (require_auth + allowlist) mints the auth code
// and returns the redirect URL back to the MCP client. Referencing
// `apexOauthHandler` here keeps test_hosting_rewrites enforcing its rewrite.
// ---------------------------------------------------------------------------

export interface OauthConsentParams {
  client_id: string;
  redirect_uri: string;
  code_challenge: string;
  state?: string;
  scope?: string;
  resource?: string;
}

/** Approve or deny an OAuth authorization request. Returns the URL to redirect
 * the browser back to the MCP client (carries `?code=…` or `?error=…`). */
export async function approveOauth(
  params: OauthConsentParams,
  approve: boolean,
): Promise<{ redirect: string }> {
  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${baseUrl}/apexOauthHandler`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders },
    body: JSON.stringify({ action: "approve", ...params, approve }),
  });
  return parseJsonSafe<{ redirect: string }>(response);
}

export const chatsApi = {
  list: (opts?: { limit?: number; includeDeleted?: boolean }) =>
    _send(`${baseUrl}/apexChatsHandler`, "POST", {
      action: "list",
      ...(opts?.limit ? { limit: opts.limit } : {}),
      ...(opts?.includeDeleted ? { includeDeleted: opts.includeDeleted } : {}),
    }) as Promise<{ chats: ChatSummary[] }>,
  get: (chatId: string) =>
    _send(`${baseUrl}/apexChatsHandler`, "POST", {
      action: "get",
      chatId,
    }) as Promise<ChatDetail>,
  delete: (chatId: string) =>
    _send(`${baseUrl}/apexChatsHandler`, "POST", { action: "delete", chatId }),
  rename: (chatId: string, title: string) =>
    _send(`${baseUrl}/apexChatsHandler`, "POST", { action: "rename", chatId, title }),
};